import { type TranslationIDs } from '@capsicum/common';
import { BaseTutorialCard, useTutorial } from '@capsicum/common-web';
import { useWalletTutorial as useWalletTutorialShared } from '@capsicum/features/nc-wallet/shared';
import { type Position, type StepType } from '@reactour/tour';
import { useEffect, useMemo } from 'react';

type UseWalletTutorialProps = {
	enabled: boolean;
};

export const useWalletTutorial = ({ enabled }: UseWalletTutorialProps = { enabled: true }) => {
	const { setup } = useTutorial();

	const {
		isBrowserSectionEnabled,
		isChartsSectionEnabled,
		isEnabled,
		isExistingUser,
		isLoading,
		isMarketplaceSectionEnabled,
		name,
		onDone
	} = useWalletTutorialShared({ enabled });

	const steps = useMemo(() => {
		const steps: Array<{
			active: boolean;
			content: TranslationIDs;
			position: Position;
			selector: StepType['selector'];
			title: TranslationIDs;
		}> = isExistingUser
			? [
					{
						active: true,
						content: 'ncWallet.tutorials.existingUsers.wallet.content',
						position: 'bottom',
						selector: '[data-tutorial="walletLobbyEntry"]',
						title: 'ncWallet.tutorials.existingUsers.wallet.title'
					},
					{
						active: isChartsSectionEnabled,
						content: 'ncWallet.tutorials.existingUsers.charts.content',
						position: 'top',
						selector: '[data-tutorial="sidebar-charts-tutorial"]',
						title: 'ncWallet.tutorials.existingUsers.charts.title'
					},
					{
						active: isMarketplaceSectionEnabled,
						content: 'ncWallet.tutorials.existingUsers.marketplace.content',
						position: 'top',
						selector: '[data-tutorial="sidebar-market-tutorial"]',
						title: 'ncWallet.tutorials.existingUsers.marketplace.title'
					},
					{
						active: isBrowserSectionEnabled,
						content: 'ncWallet.tutorials.existingUsers.browser.content',
						position: 'top',
						selector: '[data-tutorial="sidebar-browser-tutorial"]',
						title: 'ncWallet.tutorials.existingUsers.browser.title'
					},
					{
						active: true,
						content: 'ncWallet.tutorials.newUsers.rewards.content',
						position: 'top',
						selector: '[data-tutorial="sidebar-rewards-tutorial"]',
						title: 'ncWallet.tutorials.newUsers.rewards.title'
					}
				]
			: [
					{
						active: true,
						content: 'ncWallet.tutorials.newUsers.wallet.content',
						position: 'bottom',
						selector: '[data-tutorial="walletLobbyEntry"]',
						title: 'ncWallet.tutorials.newUsers.wallet.title'
					},
					{
						active: isChartsSectionEnabled,
						content: 'ncWallet.tutorials.existingUsers.charts.content',
						position: 'top',
						selector: '[data-tutorial="sidebar-charts-tutorial"]',
						title: 'ncWallet.tutorials.existingUsers.charts.title'
					},
					{
						active: isMarketplaceSectionEnabled,
						content: 'ncWallet.tutorials.existingUsers.marketplace.content',
						position: 'top',
						selector: '[data-tutorial="sidebar-market-tutorial"]',
						title: 'ncWallet.tutorials.existingUsers.marketplace.title'
					},
					{
						active: isBrowserSectionEnabled,
						content: 'ncWallet.tutorials.existingUsers.browser.content',
						position: 'top',
						selector: '[data-tutorial="sidebar-browser-tutorial"]',
						title: 'ncWallet.tutorials.existingUsers.browser.title'
					},
					{
						active: true,
						content: 'ncWallet.tutorials.newUsers.rewards.content',
						position: 'top',
						selector: '[data-tutorial="sidebar-rewards-tutorial"]',
						title: 'ncWallet.tutorials.newUsers.rewards.title'
					}
				];

		return steps
			.filter(({ active }) => active)
			.map(({ content, position, selector, title }) => ({
				content: <BaseTutorialCard content={content} title={title} />,
				position,
				selector
			}));
	}, [isExistingUser, isMarketplaceSectionEnabled, isChartsSectionEnabled, isBrowserSectionEnabled]);

	useEffect(() => {
		if (isLoading || !isEnabled) return;

		setup(name, {
			onComplete: isExistingUser ? onDone : undefined,
			preventBeginnerText: isExistingUser,
			steps
		});
	}, [name, steps, setup, onDone, isEnabled, isLoading, isExistingUser]);
};
